import { useEffect, useMemo, useState } from "react";
import { AdminApi } from "api/admin.api";
import { DynamicForm } from "./DynamicForm";
import { ColumnSchema, DynamicTableProps, TableRow } from "types/admin";
import { columnTranslations } from "../config/columnTranslations";
import styles from "../AdminPanel.module.scss";

function formatCellValue(value: any): string {
  if (value === null || value === undefined) return "—";

  if (typeof value === "boolean") return value ? "Да" : "Нет";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value.trim() ? value : "—";

  if (Array.isArray(value)) {
    if (!value.length) return "—";
    return value.join(", ");
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export const DynamicTable = ({ table }: DynamicTableProps) => {
  const [schema, setSchema] = useState<ColumnSchema[]>([]);
  const [data, setData] = useState<TableRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<TableRow | null>(null);

  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [categories, setCategories] = useState<any[]>([]);

  const isGallery = table === "gallery";

  const tableTitle = useMemo(() => columnTranslations[table] ?? table, [table]);

  const hiddenColumns = useMemo(() => {
    const hidden: string[] = [];

    if (table === "employee") {
      hidden.push("work_path_id");
    }

    if (table === "blog_post") {
      hidden.push("published_at", "is_vertical_video");
    }

    return hidden;
  }, [table]);

  const visibleSchema = useMemo(() => {
    return schema.filter((col) => !hiddenColumns.includes(col.column_name));
  }, [schema, hiddenColumns]);

  const load = async () => {
    setLoading(true);
    setErrorText(null);

    try {
      const [schemaRes, dataRes] = await Promise.all([
        AdminApi.getSchema(table),
        AdminApi.getData(table),
      ]);

      setSchema(Array.isArray(schemaRes.data) ? schemaRes.data : []);
      setData(Array.isArray(dataRes.data) ? dataRes.data : []);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message
          ? Array.isArray(e.response.data.message)
            ? e.response.data.message.join(", ")
            : String(e.response.data.message)
          : e?.message || "Неизвестная ошибка";

      setErrorText(`Ошибка загрузки таблицы "${table}": ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (table === "gallery") {
      AdminApi.getData("category").then((res) => {
        setCategories(Array.isArray(res.data) ? res.data : []);
      });
    } else {
      setCategories([]);
    }
  }, [table]);

  useEffect(() => {
    setSelectedRow(null);
    load();
    setCategoryFilter(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const handleDelete = async (rowId: any) => {
    if (!window.confirm("Удалить запись?")) return;

    setLoading(true);
    setErrorText(null);

    try {
      await AdminApi.remove(table, Number(rowId));
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message
          ? Array.isArray(e.response.data.message)
            ? e.response.data.message.join(", ")
            : String(e.response.data.message)
          : e?.message || "Неизвестная ошибка";

      setErrorText(`Ошибка удаления: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = isGallery
    ? data.filter((row) => {
        if (!categoryFilter) return true;
        return row.category_id === categoryFilter;
      })
    : data;

  return (
    <>
      <DynamicForm
        table={table}
        schema={schema}
        initialData={selectedRow}
        onSuccess={() => {
          setSelectedRow(null);
          load();
        }}
      />

      <div className={styles.table_wrapper}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>{tableTitle}</h3>

          <button className={styles.btn} onClick={load} disabled={loading} type="button">
            {loading ? "Обновляю..." : "Обновить"}
          </button>
        </div>

        {isGallery && (
          <div style={{ marginBottom: 12 }}>
            <label>Фильтр по категории</label>
            <select
              value={categoryFilter ?? ""}
              onChange={(e) =>
                setCategoryFilter(e.target.value ? Number(e.target.value) : null)
              }
              style={{ marginLeft: 10 }}
            >
              <option value="">Все категории</option>

              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {errorText ? (
          <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div>
        ) : null}

        <table>
          <thead>
            <tr>
              {visibleSchema.map((col) => (
                <th key={col.column_name}>
                  {columnTranslations[col.column_name] ?? col.column_name}
                </th>
              ))}
              <th>Действия</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleSchema.length + 1} style={{ opacity: 0.7 }}>
                  Загрузка...
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={visibleSchema.length + 1} style={{ opacity: 0.7 }}>
                  Нет записей
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr key={row.id}>
                  {visibleSchema.map((col) => {
                    const raw = row[col.column_name];
                    const text = formatCellValue(raw);

                    const isLong = typeof text === "string" && text.length > 140;
                    const shown = isLong ? text.slice(0, 140) + "…" : text;

                    return (
                      <td
                        key={col.column_name}
                        title={isLong ? text : undefined}
                        style={isLong ? { maxWidth: 520, wordBreak: "break-word" } : undefined}
                      >
                        {shown}
                      </td>
                    );
                  })}

                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.btn_primary}
                        onClick={() => setSelectedRow(row)}
                        disabled={loading}
                        type="button"
                      >
                        Редактировать
                      </button>

                      <button
                        className={styles.btn_danger}
                        onClick={() => handleDelete(row.id)}
                        disabled={loading}
                        type="button"
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};
